import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useDataManager } from '../../hooks/useDataManager';

const StoreSettings = () => {
  const { settings, updateSettings } = useDataManager();
  const [formData, setFormData] = useState(settings);
  const [saved, setSaved] = useState(false);

  // Update formData when settings are loaded from Firebase
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      setFormData(settings);
    }
  }, [settings]);

  const handleChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = () => {
    updateSettings(formData);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Store Settings</h2>
          <p className="text-gray-600 mt-1">Configure your store information</p>
        </div>
        <Button
          onClick={handleSave}
          className="bg-[#4a1a1f] hover:bg-[#5a2a2f] text-white"
        >
          <Save size={18} className="mr-2" />
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="storeName">Store Name</Label>
              <Input
                id="storeName"
                value={formData.storeName}
                onChange={(e) => handleChange(null, 'storeName', e.target.value)}
                placeholder="ABAYA STORE"
              />
            </div>
            <div>
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={formData.tagline}
                onChange={(e) => handleChange(null, 'tagline', e.target.value)}
                placeholder="SINCE 2025"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="currency">Currency</Label>
              <Input
                id="currency"
                value={formData.currency}
                onChange={(e) => handleChange(null, 'currency', e.target.value)}
                placeholder="PKR"
              />
            </div>
            <div>
              <Label htmlFor="shippingMessage">Shipping Message</Label>
              <Input
                id="shippingMessage"
                value={formData.shippingMessage}
                onChange={(e) => handleChange(null, 'shippingMessage', e.target.value)}
                placeholder="FREE DELIVERY ALL OVER KARACHI"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.contactInfo?.phone || ''}
                onChange={(e) => handleChange('contactInfo', 'phone', e.target.value)}
                placeholder="+92 300 1234567"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.contactInfo?.email || ''}
                onChange={(e) => handleChange('contactInfo', 'email', e.target.value)}
                placeholder="info@abayastore.pk"
              />
            </div>
            <div className="col-span-1">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.contactInfo?.address || ''}
                onChange={(e) => handleChange('contactInfo', 'address', e.target.value)}
                placeholder="123 Fashion Street, Karachi"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Social Media */}
      <Card>
        <CardHeader>
          <CardTitle>Social Media Links</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="facebook">Facebook</Label>
              <Input
                id="facebook"
                value={formData.socialMedia?.facebook || ''}
                onChange={(e) => handleChange('socialMedia', 'facebook', e.target.value)}
                placeholder="https://facebook.com/abayastore"
              />
            </div>
            <div>
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                value={formData.socialMedia?.instagram || ''}
                onChange={(e) => handleChange('socialMedia', 'instagram', e.target.value)}
                placeholder="https://instagram.com/abayastore"
              />
            </div>
            <div>
              <Label htmlFor="twitter">Twitter/X</Label>
              <Input
                id="twitter"
                value={formData.socialMedia?.twitter || ''}
                onChange={(e) => handleChange('socialMedia', 'twitter', e.target.value)}
                placeholder="https://twitter.com/abayastore"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sale Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Sale Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
            <Switch
              id="saleEnabled"
              checked={formData.sale?.enabled || false}
              onCheckedChange={(checked) => handleChange('sale', 'enabled', checked)}
            />
            <Label htmlFor="saleEnabled" className="cursor-pointer">
              Enable Store-Wide Sale
            </Label>
          </div>

          {formData.sale?.enabled && (
            <div className="grid grid-cols-2 gap-4 p-4 border-l-4 border-[#4a1a1f] bg-[#4a1a1f]/5 rounded">
              <div>
                <Label htmlFor="salePercentage">Discount Percentage (%)</Label>
                <Input
                  id="salePercentage"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.sale?.percentage || 0}
                  onChange={(e) => handleChange('sale', 'percentage', parseInt(e.target.value) || 0)}
                  placeholder="20"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Enter discount percentage (e.g., 20 for 20% off)
                </p>
              </div>
              <div>
                <Label htmlFor="saleLabel">Sale Badge Label</Label>
                <Input
                  id="saleLabel"
                  value={formData.sale?.label || 'SALE'}
                  onChange={(e) => handleChange('sale', 'label', e.target.value)}
                  placeholder="SALE"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Badge text shown on products (e.g., "SALE", "MEGA SALE")
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreSettings;
